import React, { useContext, useEffect, useState} from 'react';
import { customAlphabet } from 'nanoid';
import { GlobalContext } from '../../GlobalState';
import { blobTypeExtension, delayPromise } from '../../utils';
import {
  Attachment,
  Avatar,
  messageHasReactions,
  MessageOptions,
  MessageText,
  ReactionSelector,
  ReactionsList,
  useMessageContext,
  messageHasAttachments,
  useChatContext
} from 'stream-chat-react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Moment from 'moment-timezone';
import './Message.css';
import SmsIcon from '@mui/icons-material/Sms';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import { MsgReadByModal, MsgForwardModal } from '../Modals';

const nanoid = customAlphabet('1234567890ABCDEF', 20);
export const Message = () => {
  const { setGlobalState } = useContext(GlobalContext);
  const {
    // additionalMessageInputProps,
    handleAction,
    handleRetry,
    highlighted,
    isMyMessage,
    isReactionEnabled,
    message,
    onUserClick,
    onUserHover,
    reactionSelectorRef,
    showDetailedReactions,
    //readBy
  } = useMessageContext();
  const { client, channel, setActiveChannel } = useChatContext();
  const hasAttachment = messageHasAttachments(message);
  const hasReactions = messageHasReactions(message);
  const isFwdMsg = typeof message.forwarded_msg === 'object' && Array.isArray(message.forwarded_msg) === false && message.forwarded_msg !== null;
  const isPrime = /^prime:/.test(message.cid);
  const getReadByUsers = Object.keys(channel.state.read).filter(userid => message.status === 'received' && channel.cid === message.cid && message.created_at <= channel.state.read[userid]['last_read']).map(userid => channel.state.read[userid]['user']);
  const [readByUsers, setReadByUsers] = useState(getReadByUsers);
  useEffect(() => {
    let listenReadByUsers = client.on(event => {
      if (event.type === 'message.read' || event.type === 'notification.mark_read') {
        if (event.cid === channel.cid) {
          const getReadByUsers = Object.keys(channel.state.read)
          .filter(userid => message.status === 'received' && channel.cid === message.cid && message.created_at <= channel.state.read[userid]['last_read'])
          .map(userid => channel.state.read[userid]['user']);
          setReadByUsers(getReadByUsers);
        }
      }
      if (event.type === 'message.new' || event.type === 'notification.message_new') {
        if (event.message.cid === channel.cid && event.message.id === message.id) {
          const verifyReadUsers = Object.keys(channel.state.read)
          .filter(userid => new Date(event.message.created_at) <= new Date(channel.state.read[userid]['last_read']))
          .map(userid => channel.state.read[userid]['user']);
          setReadByUsers(verifyReadUsers);
        }
      }
    });
    return () => listenReadByUsers.unsubscribe();
  })
  const messageClassesPrime = /^driver/.test(message.user.id) === true || message.user.id === 'sms_service' ? 'str-chat__message str-chat__message-simple' : 'str-chat__message str-chat__message--me str-chat__message-simple str-chat__message-simple--me';
  const messageClasses = isMyMessage() ? 'str-chat__message str-chat__message--me str-chat__message-simple str-chat__message-simple--me' : 'str-chat__message str-chat__message-simple';
  const messageStatusClassesPrime = /^driver/.test(message.user.id) === true || message.user.id === 'sms_service' ? 'str-chat__message-simple-status-wrapper' : 'str-chat__message-simple-status-wrapper--me';
  const messageStatusClasses = isMyMessage() ? 'str-chat__message-simple-status-wrapper--me' : 'str-chat__message-simple-status-wrapper';
  const frmtDta = (date) => {
    const timeZone = Moment(date).tz('America/Los_Angeles').isDST() ? ' PDT' : ' PST';
    const todayIs = Moment(Date.now()).tz('America/Los_Angeles').format('YYYY MMMM DD');
    const messageDate = Moment(date).tz('America/Los_Angeles').format('YYYY MMMM DD');
    return todayIs === messageDate ? Moment(date).tz('America/Los_Angeles').format('HH:mm') + timeZone : Moment(date).tz('America/Los_Angeles').format('MMM DD HH:mm') + timeZone;
  }
  const configureJumpToForward = async () => {
    if (message.forwarded_msg.ch_type === 'direct' && typeof message.forwarded_msg.direct_members === 'object' && Array.isArray(message.forwarded_msg.direct_members) === true && message.forwarded_msg.direct_members !== null && message.forwarded_msg.direct_members.length > 0 && message.forwarded_msg.direct_members.includes(client.userID) === false) alert('You are not a member of this private conversation');
    else {
      let ch_target = client.channel(message.forwarded_msg.ch_type, message.forwarded_msg.ch_id);
      await ch_target.watch({ messages: { limit: 25 } });
      setGlobalState.setJumpTo({'msg_id': message.forwarded_msg.id, 'ch_type': message.forwarded_msg.ch_type, 'ch_id': message.forwarded_msg.ch_id, 'switch': true, 'frozen': ch_target.data.frozen});
      setActiveChannel(ch_target);
    }
  };
  const imageCustomOptions = async (e) => {
    // Function to download image onClick
    const downloadImage = async (imgURL) => {
      fetch(imgURL, {})
        .then(response => response.blob())
        .then(blob => {
          let blobUrl = window.URL.createObjectURL(blob);
          let blobExtension = blobTypeExtension(blob.type);
          let tmp_A = document.createElement('a');
          tmp_A.download = 'tmp_'+nanoid()+blobExtension;
          tmp_A.href = blobUrl;
          document.body.appendChild(tmp_A);
          tmp_A.click();
          tmp_A.remove();
      })
    };
    await delayPromise(200);
    let modalMode = null;
    if (e.target?.className === 'str-chat__message-attachment--img') modalMode = 'single';
    if (e.target?.parentNode?.className === 'str-chat__gallery-image') modalMode = 'gallery';
    if (e.target?.className === 'str-chat__gallery-placeholder') modalMode = 'gallery';
    if (e.target?.nodeName === 'P' && e.target.parentNode?.className === 'str-chat__gallery-placeholder') modalMode = 'gallery';
    let openModal_closebtn = document.querySelector('.str-chat__modal__close-button');
    if (e.target?.className === 'image-gallery-image') return openModal_closebtn.click(); // Exit On Click Image
    if (modalMode === 'single' || modalMode === 'gallery') {
      // AVOIDING MODAL OVERLAPPING WITH SIDEBAR
      let openModal = document.querySelector('.str-chat__modal.str-chat__modal--open');
      openModal.style = `width: ${openModal.offsetWidth - 65}px !important; right: 0; left: unset;`;
      // REMOVING FULLSCREEN BUTTON
      let openModal_fullscreen = document.querySelector('.image-gallery-icon.image-gallery-fullscreen-button');
      openModal_fullscreen.remove();
      // GETTING URLS
      let openModal_gallery = document.querySelector('.image-gallery-slides');
      [...openModal_gallery.childNodes].map((item, index) => {
        if (item.childElementCount === 1) {
          let imgURL = item.children[0]['attributes']['src']['nodeValue'];
          let openModal_downloadBtn = document.createElement('button');
          openModal_downloadBtn.id = 'image-gallery-download-button-'+index;
          openModal_downloadBtn.className = 'image-gallery-icon image-gallery-fullscreen-button';
          openModal_downloadBtn.innerHTML = 'DOWNLOAD';
          openModal_downloadBtn.style = 'color: black; background: white;';
          openModal_downloadBtn.addEventListener('click', async () => await downloadImage(imgURL));
          item.appendChild(openModal_downloadBtn);
          return true;
        }
        return false;
      });
    }
  };
  return (
    <>
    <div
      key={message.id}  
      className={`
				${isPrime === true ? messageClassesPrime : messageClasses}
				str-chat__message--${message.type}
				str-chat__message--${message.status}
				${message.text ? 'str-chat__message--has-text' : 'has-no-text'}
				${hasAttachment ? 'str-chat__message--has-attachment' : ''}
        ${hasReactions && isReactionEnabled ? 'str-chat__message--with-reactions' : ''}
        ${highlighted ? 'str-chat__message--highlighted' : ''}
			`.trim()}
      >
      {message.user && (<Avatar image={message.user.image} name={message.user.name || message.user.id} onClick={onUserClick} onMouseOver={onUserHover} user={message.user}/>)}
      <div className={isFwdMsg === true ? 'str-chat__message-inner str-chat__forwarded-message' : 'str-chat__message-inner'} data-testid='message-inner'
        onClick={message.status === 'failed' && message.errorStatusCode !== 403 ? () => handleRetry(message) : undefined}
        onKeyPress={message.status === 'failed' && message.errorStatusCode !== 403 ? () => handleRetry(message) : undefined}
        >
        {hasReactions && !showDetailedReactions && isReactionEnabled && <ReactionsList reverse/>}
        {isFwdMsg === true &&
            <div className='str-chat_forwarded-message-quote' onClick={configureJumpToForward}>
              <span>{'Forwarded Message'}</span>
              <span>{message.forwarded_msg.ch_type !== 'direct' ? 'Channel ' + message.forwarded_msg.ch_name : 'Private Conversation'}</span>
              <span>{'By '+message.forwarded_msg.author}</span>
            </div>
        }
        {message.attachments?.length && !message.quoted_message ? (<div onClick={imageCustomOptions}><Attachment actionHandler={handleAction} attachments={message.attachments} /></div>) : null}
        <MessageText message={message} />
        <div className={isPrime === true ? messageStatusClassesPrime : messageStatusClasses}>
          <MessageOptions />
          {showDetailedReactions && isReactionEnabled && <ReactionSelector ref={reactionSelectorRef} />}
          { (channel.type !== 'direct' && !isMyMessage() && message.user)
            ? <Typography ml={'2px'} mr={'2px'} variant='caption' sx={{fontSize: '10px', color: '#00000099'}}>{`${((message.user.name && message.user.name.length > 15) ? message.user.name.slice(0, 15)+'...' : message.user.name )|| message.user.id} @ ${frmtDta(message.created_at)}`}</Typography>
            : <Typography ml={'2px'} mr={'2px'} variant='caption' sx={{fontSize: '10px', color: '#00000099'}}>{`@ ${frmtDta(message.created_at)}`}</Typography> }
          <MsgForwardModal message={message} readByUsers={readByUsers}/>
          <MsgReadByModal msgFromUser={message.user.id} readByUsers={readByUsers}/>
          { channel.type === 'prime' && message.user.id !== 'sms_service' && message.user.id.startsWith('driver') === false && typeof message.sms_delivery_status === 'string' && message.sms_delivery_status === 'sent' && <Tooltip title={`Sent, waiting delivered status`} placement='bottom'><SmsIcon fontSize='xs' sx={{color:"orange"}} /></Tooltip> }
          { channel.type === 'prime' && message.user.id !== 'sms_service' && message.user.id.startsWith('driver') === false && typeof message.sms_delivery_status === 'string' && message.sms_delivery_status === 'delivered' && <Tooltip title={`Delivered`} placement='bottom'><SmsIcon fontSize='xs' color="primary" /></Tooltip> }
          { channel.type === 'prime' && message.user.id !== 'sms_service' && message.user.id.startsWith('driver') === false && typeof message.sms_delivery_status === 'string' && message.sms_delivery_status === 'undelivered' && <Tooltip title={`Undelivered`} placement='bottom'><SmsFailedIcon fontSize='xs' sx={{color:"#ff8787"}} /></Tooltip> }
          { channel.type === 'prime' && message.user.id !== 'sms_service' && message.user.id.startsWith('driver') === false && typeof message.sms_delivery_status === 'string' && message.sms_delivery_status !== 'sent' && message.sms_delivery_status !== 'delivered' && message.sms_delivery_status !== 'undelivered' && <Tooltip title={`Unknown/Unexpected delivered status`} placement='bottom'><SmsFailedIcon fontSize='xs' sx={{color:"#ff8787"}} /></Tooltip> }
          { channel.type === 'prime' && message.user.id !== 'sms_service' && message.user.id.startsWith('driver') === false && typeof message.sms_delivery_status !== 'string' && <Tooltip title={`Waiting sent status...`} placement='bottom'><SmsIcon fontSize='xs' sx={{color:"grey"}} /></Tooltip> }
        </div>
      </div>
    </div>
    </>
  );
};